import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { fundingPolicyTypeDefs } from '@shared';
import { deriveApplicationStatus } from '@/pages/PolicyApplicationDashboard';
import {
  putMultipleFundingSourceForClientByClientIdRequest,
  putFundingSourcesForClientByClientIdRequest,
} from '@/model';
import {
  deleteJointPolicyFromBothClients,
  findPartnerPolicyFundingSourceId,
} from '@/util/findPartnerPolicyFundingSourceId';
import produce from 'immer';

export type DeleteFundingSourceCallback = () => void;

export function useFundingSourceDelete(
  client: Client,
  policyApplications: ClientPolicyApplication[],
  onMirroredDelete?: (fundingSourceId: string, fundingSourceGroupId: string, mode: 'both' | 'client-only') => void,
) {
  const dispatch = useDispatch();

  // State
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showDeleteBlockedDialog, setShowDeleteBlockedDialog] = useState(false);
  const [deleteBlockedReason, setDeleteBlockedReason] = useState<string>('');
  const [showJointPolicyDeleteDialog, setShowJointPolicyDeleteDialog] = useState(false);
  const [jointPolicyDeleteMode, setJointPolicyDeleteMode] = useState<'both' | 'client-only'>('both');
  const [pendingDeleteAction, setPendingDeleteAction] = useState<DeleteFundingSourceCallback | null>(null);
  const [pendingJointPolicyDelete, setPendingJointPolicyDelete] = useState<{
    fundingSourceId: string;
    isJointPolicy: boolean;
  } | null>(null);
  const [showMirroredDeleteDialog, setShowMirroredDeleteDialog] = useState(false);
  const [mirroredDeleteMode, setMirroredDeleteMode] = useState<'both' | 'client-only'>('both');
  const [pendingMirroredDelete, setPendingMirroredDelete] = useState<{
    fundingSourceId: string;
    fundingSourceGroupId: string;
  } | null>(null);

  // Check if funding source can be deleted
  const checkCanDeleteFundingSource = (fundingSourceId: string): { canDelete: boolean; reason?: string } => {
    const linkedApplications = policyApplications.filter(
      app => app.fundingSourceId === fundingSourceId
    );

    for (const application of linkedApplications) {
      const { status } = deriveApplicationStatus(application);
      if (
        status === 'in_progress' ||
        status === 'submitted' ||
        status === 'completed'
      ) {
        return {
          canDelete: false,
          reason:
            'This funding source is linked to an application that is in progress, submitted, or completed.',
        };
      }
    }

    return { canDelete: true };
  };

  // Delete operations
  const deletePolicyForClientOnly = (fundingSourceId: string) => {
    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'ltcPolicy',
        fundingSourceData: null,
        fundingSourceId,
      }),
    );
  };

  const deletePolicyForBothClients = (fundingSourceId: string) => {
    deleteJointPolicyFromBothClients(
      dispatch,
      client,
      fundingSourceId,
      putMultipleFundingSourceForClientByClientIdRequest
    );
  };

  const deleteSelfFundingCallBack = (fundingSources: FundingSources) => {
    const newFundingSources = produceFundingSourcesWithoutSelfFunding(fundingSources);
    dispatch(
      putFundingSourcesForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSources: newFundingSources,
      }),
    );
  };

  const deleteAnnuityCallBack = (fundingSources: FundingSources) => {
    const newFundingSources = produceFundingSourcesWithoutAnnuity(fundingSources);
    dispatch(
      putFundingSourcesForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSources: newFundingSources,
      }),
    );
  };

  const deleteSelfFundingForClientOnly = (fundingSourceId: string) => {
    dispatch(
      putMultipleFundingSourceForClientByClientIdRequest({
        clientId: client.clientId,
        fundingSourceType: 'selfFunding',
        fundingSourceData: null,
        fundingSourceId,
      }),
    );
  };

  const deleteSelfFundingByFundingSourceIdCallBack = (fundingSourceId: string) => {
    const selfFunding = client.multipleFundingSources.selfFunding[fundingSourceId];
    const isMirrored = selfFunding?.partnerAction === 'mirrored' && selfFunding?.fundingSourceGroupId;

    if (isMirrored && client.mutableClientPartner) {
      setPendingMirroredDelete({
        fundingSourceId,
        fundingSourceGroupId: selfFunding.fundingSourceGroupId!,
      });
      setShowMirroredDeleteDialog(true);
    } else {
      deleteSelfFundingForClientOnly(fundingSourceId);
    }
  };

  const deletePolicyByFundingSourceIdCallBack = (fundingSourceId: string) => {
    const { canDelete, reason } = checkCanDeleteFundingSource(fundingSourceId);

    if (!canDelete) {
      setDeleteBlockedReason(reason || 'This funding source cannot be deleted.');
      setShowDeleteBlockedDialog(true);
      return;
    }

    const policy = client.multipleFundingSources.ltcPolicy[fundingSourceId];
    const isJoint = policy?.policyType && fundingPolicyTypeDefs[policy.policyType]?.isJoint === true;

    if (isJoint && client.mutableClientPartner && policy?.insuranceQuoteId) {
      const partnerFundingSourceId = findPartnerPolicyFundingSourceId(
        client.mutableClientPartner,
        { insuranceQuoteId: policy.insuranceQuoteId }
      );

      if (partnerFundingSourceId) {
        setPendingJointPolicyDelete({ fundingSourceId, isJointPolicy: true });
        setShowJointPolicyDeleteDialog(true);
      } else {
        deletePolicyForClientOnly(fundingSourceId);
      }
    } else {
      deletePolicyForClientOnly(fundingSourceId);
    }
  };

  // Dialog handlers
  const handleDeleteClick = (deleteCallback: DeleteFundingSourceCallback) => {
    setPendingDeleteAction(() => deleteCallback);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteAction) {
      pendingDeleteAction();
    }
    setShowDeleteConfirmation(false);
    setPendingDeleteAction(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setPendingDeleteAction(null);
  };

  const handleMirroredDeleteCancel = () => {
    setShowMirroredDeleteDialog(false);
    setPendingMirroredDelete(null);
    setMirroredDeleteMode('both');
  };

  const handleMirroredDeleteConfirm = (mode: 'both' | 'client-only') => {
    if (!pendingMirroredDelete) return;

    const { fundingSourceId, fundingSourceGroupId } = pendingMirroredDelete;

    if (onMirroredDelete) {
      onMirroredDelete(fundingSourceId, fundingSourceGroupId, mode);
    } else {
      deleteSelfFundingForClientOnly(fundingSourceId);
    }

    setShowMirroredDeleteDialog(false);
    setPendingMirroredDelete(null);
    setMirroredDeleteMode('both');
  };

  const handleJointPolicyDeleteCancel = () => {
    setShowJointPolicyDeleteDialog(false);
    setPendingJointPolicyDelete(null);
    setJointPolicyDeleteMode('both');
  };

  const handleJointPolicyDeleteConfirm = (mode: 'both' | 'client-only') => {
    if (pendingJointPolicyDelete) {
      if (mode === 'both') {
        deletePolicyForBothClients(pendingJointPolicyDelete.fundingSourceId);
      } else {
        deletePolicyForClientOnly(pendingJointPolicyDelete.fundingSourceId);
      }
    }
    setShowJointPolicyDeleteDialog(false);
    setPendingJointPolicyDelete(null);
    setJointPolicyDeleteMode('both');
  };

  return {
    // State
    showDeleteConfirmation,
    showDeleteBlockedDialog,
    setShowDeleteBlockedDialog,
    deleteBlockedReason,
    showJointPolicyDeleteDialog,
    jointPolicyDeleteMode,
    setJointPolicyDeleteMode,
    showMirroredDeleteDialog,
    mirroredDeleteMode,
    setMirroredDeleteMode,

    // Delete callbacks
    deleteSelfFundingCallBack,
    deleteSelfFundingByFundingSourceIdCallBack,
    deleteAnnuityCallBack,
    deletePolicyByFundingSourceIdCallBack,

    // Dialog handlers
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleJointPolicyDeleteCancel,
    handleJointPolicyDeleteConfirm,
    handleMirroredDeleteCancel,
    handleMirroredDeleteConfirm,
  };
}

// Helper functions
function produceFundingSourcesWithoutSelfFunding(
  fundingSources: FundingSources,
): FundingSources {
  return produce(fundingSources, draftFundingSources => {
    draftFundingSources.selfFunding.existingAssetsValue = null;
    draftFundingSources.selfFunding.monthlyContributionAmount = null;
    draftFundingSources.selfFunding.annualRateOfReturn = null;
    draftFundingSources.selfFunding.hasSelfFunding = false;
    draftFundingSources.selfFunding.gainsTaxRate = null;
    draftFundingSources.selfFunding.monthlyIncome = null;
    draftFundingSources.selfFunding.addedByAdvisorFullName = undefined;
    draftFundingSources.selfFunding.addedByAdvisorId = undefined;
  });
}

function produceFundingSourcesWithoutAnnuity(
  fundingSources: FundingSources,
): FundingSources {
  return produce(fundingSources, draftFundingSources => {
    draftFundingSources.annuity.annuityExpectedPaymentStartAge = null;
    draftFundingSources.annuity.annuityExpectedPaymentEndAge = null;
    draftFundingSources.annuity.annuityAnnualPayOut = null;
    draftFundingSources.annuity.hasAnnuity = false;
    draftFundingSources.annuity.annuityPurchasePrice = null;
    draftFundingSources.annuity.annuityAnnualPayoutForLtc = null;
    draftFundingSources.annuity.annuityAnnualPayoutForLtcPeriodYears = null;
    draftFundingSources.annuity.addedByAdvisorFullName = undefined;
    draftFundingSources.annuity.addedByAdvisorId = undefined;
  });
}
